import * as React from 'react'

import { CircleAlertIcon, XIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/Button/Button'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import StruxhubInputHelperText from './components/StruxhubInputHelperText'
import StruxhubInputLabel from './components/StruxhubInputLabel'

// localization: no text to translate

export interface ISharedProps {
  label?: string
  /** Shown behind an info icon beside the label. Omit for no icon. */
  labelTooltip?: string
  value?: string
  isRequired?: boolean
  isDisabled?: boolean
  isReadOnly?: boolean
  isChanged?: boolean
  isValid?: boolean
  validationMessage?: string
  validationMessages?: string[]
  additionalHelperText?: string
  /** Shown under the input, in place of an empty helper row. */
  note?: React.ReactNode
  className?: string

  isMinimalisticMode?: boolean
  hasShortWidth?: boolean
  isRequiredTextHidden?: boolean
  isLabelHidden?: boolean
  isHelperTextCondensed?: boolean
  isHelperTextHidden?: boolean
  hideBottomLabel?: boolean
  shouldLineUpLabel?: boolean
  ignoreValueForValidation?: boolean
  isTopMarginForced?: boolean

  onValueReset?: () => void
  customRightIcon?: React.ReactNode
  testId?: string
}

interface IProps extends ISharedProps {
  children: (
    isValueInvalid: boolean,
    isInFocus: boolean,
    onFocus: () => void,
    onBlur: () => void,
  ) => React.ReactNode
}

const RIGHT_PADDING = 8

@observer
export default class BaseStruxhubInput extends React.Component<IProps> {
  public static defaultProps = {
    isValid: true,
  }

  @observable private isInFocus: boolean = false

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const {
      label,
      labelTooltip,
      isRequired,
      isChanged,
      additionalHelperText,
      validationMessage,
      hasShortWidth,
      value,
      isDisabled,
      isReadOnly,
      isMinimalisticMode,
      isRequiredTextHidden,
      isLabelHidden = false,
      isHelperTextCondensed = false,
      isHelperTextHidden = false,
      children,
      className,
      shouldLineUpLabel,
      hideBottomLabel = false,
      validationMessages,
      isTopMarginForced = false,
      note,
    } = this.props

    const helperText = !isMinimalisticMode &&
      !isHelperTextHidden &&
      !hideBottomLabel && (
        <StruxhubInputHelperText
          isChanged={isChanged}
          isValid={!this.isValueInvalid}
          additionalHelperText={additionalHelperText}
          validationMessage={validationMessage}
          validationMessages={validationMessages}
          isCondensed={isHelperTextCondensed}
          isHiddenWhenEmpty={!!note}
        />
      )

    return (
      <div
        className={classList({
          col: true,
          mt8: isTopMarginForced || !isHelperTextCondensed,
          'inactive-element': isDisabled,
          'unclickable-element': isReadOnly,
          [className]: !!className,
        })}
      >
        {!isMinimalisticMode && !isLabelHidden && (
          <StruxhubInputLabel
            label={label}
            labelTooltip={labelTooltip}
            value={value}
            isRequired={isRequired}
            isValid={!this.isValueInvalid}
            hasShortWidth={hasShortWidth}
            isRequiredTextHidden={isRequiredTextHidden}
            shouldLineUpLabel={shouldLineUpLabel}
          />
        )}
        <div className="row mb4 relative">
          {children(
            this.isValueInvalid,
            this.isInFocus,
            this.onFocus,
            this.onBlur,
          )}
          {this.actionIcons}
        </div>
        {note ? (
          // The bottom margin gives back the helper row the note took.
          <div className="tw:mb-5 tw:flex tw:flex-col tw:gap-1">
            {helperText}
            <div className="tw:px-2">{note}</div>
          </div>
        ) : (
          helperText
        )}
      </div>
    )
  }

  private get actionIcons(): JSX.Element {
    const { onValueReset, customRightIcon } = this.props

    if (customRightIcon) {
      return (
        <div
          className="absolute text large light no-grow opacity5 row x-center y-center"
          style={{ right: RIGHT_PADDING }}
        >
          {customRightIcon}
        </div>
      )
    }

    if (!onValueReset) {
      return null
    }

    return (
      <>
        {this.shouldShowClearIcon && (
          <Button
            variant="ghost"
            size="icon-xs"
            className="tw:absolute tw:text-muted-foreground"
            aria-label={Localization.translator.clear}
            onMouseDown={onValueReset}
            onClick={onValueReset}
            style={{ right: RIGHT_PADDING }}
          >
            <XIcon />
          </Button>
        )}
        {this.shouldShowAlertIcon && (
          <CircleAlertIcon
            size={16}
            className="tw:absolute tw:text-destructive"
            style={{ right: RIGHT_PADDING }}
          />
        )}
      </>
    )
  }

  private get shouldShowClearIcon(): boolean {
    const { onValueReset, value } = this.props
    return !!onValueReset && this.isInFocus && !!value
  }

  private get shouldShowAlertIcon(): boolean {
    return !!this.props.onValueReset && !this.isInFocus && this.isValueInvalid
  }

  private get isValueInvalid(): boolean {
    const { value, isValid, ignoreValueForValidation } = this.props
    return ignoreValueForValidation ? !isValid : !!value && !isValid
  }

  @action.bound
  private onFocus() {
    this.isInFocus = true
  }

  @action.bound
  private onBlur() {
    this.isInFocus = false
  }
}
